import { Box, Container, Typography, Button, Divider } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import GlobalSearch from '@components/search/GlobalSearch';
import EpisodeList from '@components/episode/EpisodeList';
import PodcastCoverArt from '@components/shared/PodcastCoverArt';
import EmptyState from '@components/shared/EmptyState';

export default function HomePageView({
  episodes,
  recentEpisodes,
  feedMetadata,
  loading,
  error,
  showSearchResults,
  globalSearch,
  onPlayClick,
  onViewAll,
  hasMoreEpisodes
}) {
  if (loading) {
    return <EmptyState type="loading" message="Loading episodes..." />;
  }

  if (error) {
    return <EmptyState type="error" message={error} />;
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          py: { xs: 3, md: 6 },
          textAlign: 'center',
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <PodcastCoverArt
            imageUrl={feedMetadata?.image}
            alt={feedMetadata?.title}
            size={160}
            variant="rounded"
            sx={{ width: { xs: 128, sm: 160 }, height: { xs: 128, sm: 160 } }}
            loading="eager"
          />
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.125rem' },
            lineHeight: { xs: 1.6, sm: 1.8 }
          }}
        >
          {feedMetadata?.description ||
            'A family interview podcast capturing stories, wisdom, and experiences from family members to preserve them for future generations.'}
        </Typography>
      </Box>

      <Box sx={{ py: { xs: 3, sm: 4 } }}>
        <Box sx={{ mb: { xs: 4, sm: 5 } }}>
          <GlobalSearch {...globalSearch} />
        </Box>

        {!showSearchResults && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                mb: 3
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ mb: 0.5, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
                >
                  Recent Episodes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {episodes.length === 0
                    ? 'No episodes yet'
                    : `${episodes.length} episode${episodes.length !== 1 ? 's' : ''} available`}
                </Typography>
              </Box>

              {hasMoreEpisodes && (
                <Button
                  variant="text"
                  endIcon={<ArrowForward />}
                  onClick={onViewAll}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  View all episodes
                </Button>
              )}
            </Box>

            <Divider sx={{ mb: 4 }} />

            {recentEpisodes.length === 0 ? (
              <EmptyState type="empty" message="No episodes available yet." />
            ) : (
              <>
                <EpisodeList
                  episodes={recentEpisodes}
                  onPlayClick={onPlayClick}
                />

                {hasMoreEpisodes && (
                  <Box sx={{ mt: 5, textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={onViewAll}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem'
                      }}
                    >
                      View all {episodes.length} episodes
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}
